<template>
  <section ref="conversationRef" class="ai-conversation">
    <!-- 对话标题栏只向父组件转发新建和关闭操作。 -->
    <ConversationHeader :t="t" :title="title" :mobile-menu-open="mobilePanelOpen" @home="$emit('home')"
      @new-chat="$emit('new-chat')" @close="$emit('close')"
      @mobile-menu="mobilePanelOpen = true"
      @regular-search="$emit('regular-search', $event)" />

    <!-- 消息容器只负责排列消息；具体回答和业务结果由各自组件渲染。 -->
    <div class="conversation-messages">
      <article
        v-for="message in messages"
        :key="message.id"
        :ref="element => setMessageElement(message.id, element)"
        class="conversation-message"
        :class="`conversation-message--${message.role}`"
      >
        <!-- 用户消息使用右侧问题气泡。 -->
        <div v-if="message.role === 'user'" class="conversation-message__question">
          {{ message.content }}
        </div>
        <div v-else class="conversation-message__answer">
          <!-- 请求中显示思考文案，完成后显示本轮客户端等待耗时。 -->
          <button v-if="message.thinkingKey" type="button" class="conversation-message__thinking">
            {{ getThinkingText(message) }}
            <CustomImg :src="arrowIcon" alt="" />
          </button>
          <!-- 接口返回前显示等待动画；返回后切换为逐步输出的 Markdown 正文。 -->
          <div v-if="message.loading" class="conversation-message__loading" aria-live="polite">
            <span></span><span></span><span></span>
          </div>
          <div v-else class="conversation-message__content">
            <AnswerMarkdown :content="message.content" :streaming="message.streaming" />

            <!-- 结构数据在正文输出完成后才会写入消息，因此卡片不会提前出现。 -->
            <ProductResults
              v-if="message.products?.length"
              :products="message.products"
              :t="t"
            />

            <ShowroomResults v-if="message.showrooms?.length" :showrooms="message.showrooms" />

            <AnswerFeedback
              v-if="message.runId && !message.streaming"
              :run-id="message.runId"
              :t="t"
            />

            <!-- 推荐问题由父组件写回输入框，不在这里直接发起新请求。 -->
            <div v-if="message.suggestedQuestions?.length" class="conversation-suggestions">
              <button
                v-for="question in message.suggestedQuestions"
                :key="question"
                type="button"
                @click="$emit('suggest-question', question)"
              >
                {{ question }}
              </button>
            </div>
          </div>
        </div>
      </article>
    </div>

    <ConversationQuestionNav
      v-if="questionItems.length > 1"
      :items="questionItems"
      :active-id="activeQuestionId"
      @select="scrollToQuestion"
    />

    <Transition name="back-to-bottom">
      <button
        v-if="showBackToBottom"
        type="button"
        class="back-to-bottom"
        :aria-label="t('ai.chat.scrollToBottom')"
        :title="t('ai.chat.scrollToBottom')"
        @click="scrollToBottom"
      >
        <svg viewBox="0 0 1024 1024" aria-hidden="true">
          <path d="M511.99 141.21c10.23 0 18.95 3.61 26.21 10.82 7.25 7.21 10.87 15.97 10.87 26.27v577.89l196.16-196.44c7.1-7.01 15.9-10.61 26.34-10.61 10.62 0 19.43 3.51 26.51 10.51 7.04 7.1 10.55 15.97 10.55 26.57 0 10.41-3.54 19.16-10.69 26.38L538.38 872.08c-7.15 7.21-15.93 10.71-26.37 10.71-10.46 0-19.24-3.5-26.37-10.71L226.09 612.59c-7.14-7.21-10.72-15.97-10.72-26.38 0-10.61 3.52-19.47 10.57-26.57 7.05-7.01 15.89-10.51 26.51-10.51 10.42 0 19.22 3.61 26.37 10.61l196.11 196.44V178.3c0-10.3 3.61-19.06 10.89-26.27 7.24-7.21 15.99-10.82 26.22-10.82h-.05z" />
        </svg>
      </button>
    </Transition>

    <MobileSidePanel :open="mobilePanelOpen" @close="mobilePanelOpen = false">
      <nav class="mobile-question-list" aria-label="Conversation questions">
        <button
          v-for="item in questionItems"
          :key="item.id"
          type="button"
          class="mobile-question-list__item"
          :class="{ 'is-active': item.id === activeQuestionId }"
          :aria-current="item.id === activeQuestionId ? 'location' : undefined"
          @click="selectMobileQuestion(item.id)"
        >
          {{ item.label }}
        </button>
      </nav>
    </MobileSidePanel>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { ComponentPublicInstance } from 'vue';
import CustomImg from '@/components/CustomImg.vue';
import arrowIcon from '@/assets/images/ai/arr.svg';
import AnswerMarkdown from './AnswerMarkdown.vue';
import AnswerFeedback from './AnswerFeedback.vue';
import ConversationHeader from './ConversationHeader.vue';
import ConversationQuestionNav from './ConversationQuestionNav.vue';
import MobileSidePanel from './MobileSidePanel.vue';
import ProductResults from './ProductResults.vue';
import ShowroomResults from './ShowroomResults.vue';
import type { AiMessage, Translate } from './types';

const props = defineProps<{
  t: Translate;
  title: string;
  messages: AiMessage[];
}>();
const { t } = props;

defineEmits<{
  'home': [];
  'new-chat': [];
  'close': [];
  'regular-search': [query: string];
  'suggest-question': [question: string];
}>();

const conversationRef = ref<HTMLElement | null>(null);
const activeQuestionId = ref('');
const mobilePanelOpen = ref(false);
const showBackToBottom = ref(false);
const messageElements = new Map<string, HTMLElement>();
let scrollContainer: HTMLElement | null = null;
let scrollFrame = 0;
const BACK_TO_BOTTOM_THRESHOLD = 600;

const questionItems = computed(() => props.messages
  .filter(message => message.role === 'user')
  .map(message => ({ id: message.id, label: message.content })));

const setMessageElement = (
  id: string,
  element: Element | ComponentPublicInstance | null,
) => {
  if (element instanceof HTMLElement) {
    messageElements.set(id, element);
  } else {
    messageElements.delete(id);
  }
};

// 当前轮次取滚动容器顶部判断线之前的最后一个用户问题。
const updateActiveQuestion = () => {
  scrollFrame = 0;
  if (!scrollContainer) {
    showBackToBottom.value = false;
    activeQuestionId.value = '';
    return;
  }

  const distanceFromBottom = scrollContainer.scrollHeight
    - scrollContainer.scrollTop
    - scrollContainer.clientHeight;
  showBackToBottom.value = distanceFromBottom > BACK_TO_BOTTOM_THRESHOLD;

  if (!questionItems.value.length) {
    activeQuestionId.value = '';
    return;
  }

  // 最后一轮内容不足以顶到判断线时，滚动到底仍应视为正在查看最新问题。
  if (distanceFromBottom <= 2) {
    activeQuestionId.value = questionItems.value[questionItems.value.length - 1]?.id ?? '';
    return;
  }

  const threshold = scrollContainer.getBoundingClientRect().top + 120;
  let currentId = questionItems.value[0].id;

  for (const item of questionItems.value) {
    const element = messageElements.get(item.id);
    if (!element) continue;
    if (element.getBoundingClientRect().top > threshold) break;
    currentId = item.id;
  }

  activeQuestionId.value = currentId;
};

const scrollToBottom = () => {
  scrollContainer?.scrollTo({
    top: scrollContainer.scrollHeight,
    behavior: 'smooth',
  });
};

const scheduleActiveQuestionUpdate = () => {
  if (scrollFrame) return;
  scrollFrame = window.requestAnimationFrame(updateActiveQuestion);
};

const scrollToQuestion = (id: string, behavior: ScrollBehavior = 'smooth') => {
  const element = messageElements.get(id);
  if (!element) return;
  activeQuestionId.value = id;
  element.scrollIntoView({ behavior, block: 'start' });
};

const selectMobileQuestion = (id: string) => {
  mobilePanelOpen.value = false;
  scrollToQuestion(id);
};

const connectScrollContainer = () => {
  const container = conversationRef.value?.closest('.ai-main-content');
  if (!(container instanceof HTMLElement) || container === scrollContainer) return;

  scrollContainer?.removeEventListener('scroll', scheduleActiveQuestionUpdate);
  scrollContainer = container;
  scrollContainer.addEventListener('scroll', scheduleActiveQuestionUpdate, { passive: true });
  updateActiveQuestion();
};

watch(
  () => questionItems.value.map(item => item.id),
  async (questionIds, previousQuestionIds) => {
    await nextTick();
    connectScrollContainer();

    // 新问题一经发出就成为当前轮次，并让正文与两种问题导航同步定位。
    if (questionIds.length > previousQuestionIds.length) {
      const latestQuestionId = questionIds[questionIds.length - 1];
      if (latestQuestionId) scrollToQuestion(latestQuestionId, 'auto');
      return;
    }

    updateActiveQuestion();
  },
  { flush: 'post' },
);

onMounted(async () => {
  await nextTick();
  connectScrollContainer();
});

onBeforeUnmount(() => {
  scrollContainer?.removeEventListener('scroll', scheduleActiveQuestionUpdate);
  if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
  messageElements.clear();
});

const getThinkingText = (message: AiMessage): string => {
  if (!message.thinkingKey) return '';
  if (message.loading || message.thinkingDurationMs === undefined) return t(message.thinkingKey);

  const seconds = (message.thinkingDurationMs / 1000).toFixed(1);
  return t('ai.chat.thinkingElapsed').replace('{seconds}', seconds);
};
</script>

<style scoped lang="scss">
.ai-conversation {
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  min-height: calc(100% - 190px);
}

.conversation-messages {
  flex: 0 0 auto;
  width: min(780px, calc(100% - 40px));
  margin: 0 auto;
  padding: 30px 0;
}

.conversation-message {
  margin-bottom: 28px;
  scroll-margin-top: 90px;
}

.conversation-message__question {
  margin-left: auto;
  width: fit-content;
  max-width: 76%;
  padding: 13px 20px;
  border-radius: 18px 18px 4px 18px;
  background: #f2f2f2;
  line-height: 1.6;
}

.conversation-message__answer { width: min(860px, 100%); }

.conversation-message__thinking {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: 0;
  color: #777;
  background: transparent;
  cursor: pointer;

  img { width: 11px; }
}

.conversation-message__content {
  margin-top: 14px;
  color: #444;
  line-height: 1.85;

}

.conversation-message__loading {
  display: flex;
  gap: 6px;
  margin-top: 16px;

  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--green);
    animation: conversation-loading 1.1s infinite ease-in-out;

    &:nth-child(2) { animation-delay: .15s; }
    &:nth-child(3) { animation-delay: .3s; }
  }
}

@keyframes conversation-loading {
  0%, 60%, 100% { opacity: .25; transform: translateY(0); }
  30% { opacity: 1; transform: translateY(-4px); }
}

.conversation-suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 20px;

  button {
    padding: 8px 13px;
    border: 1px solid rgba(88, 189, 110, .55);
    border-radius: 999px;
    color: #397c49;
    background: #f7fcf8;
    cursor: pointer;
  }
}

.mobile-question-list {
  display: flex;
  flex-direction: column;
  padding: 0 12px 24px;
}

.mobile-question-list__item {
  width: 100%;
  padding: 8px 0;
  border: 0;
  color: #222;
  background: transparent;
  font-size: 16px;
  line-height: 1.45;
  text-align: left;
  overflow-wrap: anywhere;
  cursor: pointer;

  &.is-active { color: var(--green); }
}

.back-to-bottom {
  position: fixed;
  bottom: 225px;
  left: 50%;
  z-index: 20;
  display: grid;
  width: 38px;
  aspect-ratio: 1 / 1;
  padding: 8px;
  border: 1px solid #dedede;
  border-radius: 50%;
  color: #202020;
  background: #fff;
  box-shadow: 0 4px 10px rgba(0, 0, 0, .14);
  cursor: pointer;
  place-items: center;
  transform: translateX(-50%);
  transition: border-color .2s ease, box-shadow .2s ease, transform .2s ease;

  svg {
    display: block;
    width: 100%;
    height: 100%;
    fill: currentColor;
  }

  &:hover,
  &:focus-visible {
    border-color: #c7c7c7;
    box-shadow: 0 5px 14px rgba(0, 0, 0, .2);
    outline: none;
    transform: translate(-50%, -2px);
  }
}

.back-to-bottom-enter-active,
.back-to-bottom-leave-active { transition: opacity .18s ease, transform .18s ease; }

.back-to-bottom-enter-from,
.back-to-bottom-leave-to {
  opacity: 0;
  transform: translate(-50%, 8px);
}

@media (max-width: 900px) {
  .conversation-messages { width: calc(100% - 24px); }
}

@media (max-width: 600px) {
  .conversation-messages { padding: 20px 0; }
  .conversation-message__question { max-width: 90%; }
  .back-to-bottom { bottom: 190px; }
}

@media (prefers-reduced-motion: reduce) {
  .back-to-bottom,
  .back-to-bottom-enter-active,
  .back-to-bottom-leave-active { transition: none; }
}
</style>
